// src/components/search/FiltersSidebar.tsx
"use client";

import { useEffect, useMemo, useState } from "react";

// ✅ Keep backward-compatible exported type names used elsewhere
export type FiltersState = {
  q: string;
  min: string;
  max: string;
  brands: string[];
  conditions: string[];
};

export type OptionCount = {
  value: string;
  count: number;
};

// ✅ Your existing names can still exist (optional, but harmless)
export type FiltersValue = FiltersState;

export type Facets = {
  brands: Record<string, number>;
  conditions: Record<string, number>;
};

export default function FiltersSidebar({
  value,
  facets,
  onChange,
  onApply,
  onReset,
}: {
  value: FiltersState | null | undefined;
  facets: Facets | null | undefined;
  onChange: (next: FiltersState) => void;
  onApply: () => void;
  onReset: () => void;
}) {
  const safeValue: FiltersState = useMemo(
    () => ({
      q: (value?.q ?? "").toString(),
      min: (value?.min ?? "").toString(),
      max: (value?.max ?? "").toString(),
      brands: Array.isArray(value?.brands) ? value!.brands : [],
      conditions: Array.isArray(value?.conditions) ? value!.conditions : [],
    }),
    [value]
  );

  const [local, setLocal] = useState<FiltersState>(safeValue);

  useEffect(() => {
    setLocal(safeValue);
  }, [
    safeValue.q,
    safeValue.min,
    safeValue.max,
    safeValue.brands.join("|"),
    safeValue.conditions.join("|"),
  ]);

  const safeFacets: Facets = useMemo(
    () => ({
      brands: facets?.brands ?? {},
      conditions: facets?.conditions ?? {},
    }),
    [facets]
  );

  const condKeys = Object.keys(safeFacets.conditions);
  const brandKeys = Object.keys(safeFacets.brands);

  function toggle(list: string[], item: string) {
    return list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
  }

  function update(next: Partial<FiltersState>) {
    const merged: FiltersState = {
      ...local,
      ...next,
      brands: Array.isArray(next.brands) ? next.brands : local.brands,
      conditions: Array.isArray(next.conditions) ? next.conditions : local.conditions,
    };
    setLocal(merged);
    onChange(merged);
  }

  return (
    <aside
      className="w-full rounded-xl border p-4 shadow-sm lg:w-64
                 border-neutral-200 bg-white text-neutral-900
                 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100"
    >
      <div className="text-sm font-semibold">Filters</div>

      <div className="mt-4 space-y-4 text-sm">
        {/* Search */}
        <div>
          <div className="font-medium">Search within results</div>
          <input
            value={local.q}
            onChange={(e) => update({ q: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") onApply();
            }}
            placeholder="Type to refine..."
            className="mt-2 h-9 w-full rounded-md border px-3 outline-none
                       border-neutral-200 bg-white text-neutral-900 placeholder:text-neutral-400
                       focus:ring-2 focus:ring-neutral-900/10
                       dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100 dark:placeholder:text-neutral-500
                       dark:focus:ring-neutral-100/10"
          />
        </div>

        {/* Condition */}
        <div className="border-t pt-4 border-neutral-200 dark:border-neutral-800">
          <div className="font-medium">Condition</div>

          <div className="mt-2 space-y-2 text-neutral-700 dark:text-neutral-300">
            {condKeys.length === 0 ? (
              <div className="text-xs text-neutral-500 dark:text-neutral-400">
                No conditions available
              </div>
            ) : (
              condKeys.map((c) => (
                <label key={c} className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-neutral-900 dark:accent-neutral-100"
                      checked={local.conditions.includes(c)}
                      onChange={() => update({ conditions: toggle(local.conditions, c) })}
                    />
                    <span className="text-sm">{c}</span>
                  </span>

                  <span
                    className="rounded-full border px-2 py-0.5 text-xs
                               border-neutral-200 text-neutral-600
                               dark:border-neutral-700 dark:text-neutral-300"
                  >
                    {safeFacets.conditions[c]}
                  </span>
                </label>
              ))
            )}
          </div>
        </div>

        {/* Brand */}
        <div className="border-t pt-4 border-neutral-200 dark:border-neutral-800">
          <div className="font-medium">Brand</div>

          <div className="mt-2 space-y-2 text-neutral-700 dark:text-neutral-300">
            {brandKeys.length === 0 ? (
              <div className="text-xs text-neutral-500 dark:text-neutral-400">
                No brands available
              </div>
            ) : (
              brandKeys.map((b) => (
                <label key={b} className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-neutral-900 dark:accent-neutral-100"
                      checked={local.brands.includes(b)}
                      onChange={() => update({ brands: toggle(local.brands, b) })}
                    />
                    <span className="text-sm">{b}</span>
                  </span>

                  <span
                    className="rounded-full border px-2 py-0.5 text-xs
                               border-neutral-200 text-neutral-600
                               dark:border-neutral-700 dark:text-neutral-300"
                  >
                    {safeFacets.brands[b]}
                  </span>
                </label>
              ))
            )}
          </div>
        </div>

        {/* Price */}
        <div className="border-t pt-4 border-neutral-200 dark:border-neutral-800">
          <div className="font-medium">Price</div>

          <div className="mt-2 grid grid-cols-2 gap-2">
            <input
              value={local.min}
              onChange={(e) => update({ min: e.target.value })}
              placeholder="Min"
              inputMode="decimal"
              className="h-9 rounded-md border px-2 outline-none
                         border-neutral-200 bg-white text-neutral-900 placeholder:text-neutral-400
                         focus:ring-2 focus:ring-neutral-900/10
                         dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100 dark:placeholder:text-neutral-500
                         dark:focus:ring-neutral-100/10"
            />
            <input
              value={local.max}
              onChange={(e) => update({ max: e.target.value })}
              placeholder="Max"
              inputMode="decimal"
              className="h-9 rounded-md border px-2 outline-none
                         border-neutral-200 bg-white text-neutral-900 placeholder:text-neutral-400
                         focus:ring-2 focus:ring-neutral-900/10
                         dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100 dark:placeholder:text-neutral-500
                         dark:focus:ring-neutral-100/10"
            />
          </div>

          <button
            onClick={onApply}
            type="button"
            className="mt-3 h-9 w-full rounded-md text-sm font-medium transition
                       bg-neutral-900 text-white hover:bg-neutral-800
                       dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-neutral-200"
          >
            Apply
          </button>

          <button
            onClick={onReset}
            type="button"
            className="mt-2 h-9 w-full rounded-md border text-sm font-medium transition
                       border-neutral-200 bg-white text-neutral-900 hover:bg-neutral-50
                       dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100 dark:hover:bg-neutral-800"
          >
            Reset
          </button>
        </div>
      </div>
    </aside>
  );
}
